import { CheckIcon } from 'lucide-react-native';
import { useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { twJoin } from 'tailwind-merge';

import { ExpertMultiSelect } from '@/components/ExpertMultiSelect';
import { SupportForm } from '@/components/supportForm';
import { Checkbox, CheckboxIcon, CheckboxIndicator, CheckboxLabel } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { LinkText } from '@/components/ui/link';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { GENERAL_EMAIL_ADDRESS, GENERAL_EMAIL_LINK } from '@/constants/links';
import { useSupportPrompt } from '@/features/support-contact';
import { useEmailLink } from '@/hooks/useEmailLink';
import { useExpertSelectionField } from '@/hooks/useExpertSelectionField';
import { InquiryDraftFormData } from '@/hooks/useInquiryForm';
import { type ExpertProfile } from '@/util/mapDirectoryProfiles';

interface AdditionalExpertsSectionProps {
  control: Control<InquiryDraftFormData>;
  errors: FieldErrors<InquiryDraftFormData>;
  setValue: UseFormSetValue<InquiryDraftFormData>;
  isDisabled: boolean;
  cancerFocusId: string;
  enableExpertSelection: boolean;
  filteredExperts: ExpertProfile[];
  isLoadingFilteredExperts: boolean;
  isFetchingFilteredExperts?: boolean;
  isDraft?: boolean;
  cancerTypeLabel?: string;
  currentUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

/**
 * Additional Experts Selection Section
 *
 * @overview
 * Progressive disclosure component for selecting additional experts beyond the primary expert.
 * Only enabled after cancer type selection and visually de-emphasized to discourage as primary flow.
 * Shows conditional UX when only one expert is available for the selected cancer type.
 *
 * @behavior
 * - Checkbox to enable/disable additional expert selection
 * - Multi-select dropdown with filtered experts (excluding primary expert)
 * - Conditional rendering based on cancer type selection
 * - Automatic clearing when cancer type changes
 * - Client-side alphabetical sorting of expert names
 * - Disabled state with informative message when no additional experts available
 * - Support form integration for expert recommendations
 */
export function AdditionalExpertsSection({
  control,
  errors,
  setValue,
  isDisabled,
  cancerFocusId,
  enableExpertSelection,
  filteredExperts,
  isLoadingFilteredExperts,
  isFetchingFilteredExperts = false,
  isDraft = false,
  cancerTypeLabel,
  currentUser,
}: AdditionalExpertsSectionProps) {
  const supportPrompt = useSupportPrompt({
    source: 'additional_experts',
    draft: 'I’d like to recommend an expert: ',
  });

  const { openEmailLink } = useEmailLink();

  const isSectionDisabled = isDisabled || !cancerFocusId;

  const {
    isExpertMultiSelectOpen,
    setIsExpertMultiSelectOpen,
    realExperts,
    hasExperts: hasAdditionalExperts,
    handleSendToAdditionalChange,
    handleMultiSelectClose: handleExpertMultiSelectClose,
  } = useExpertSelectionField({
    control,
    setValue,
    cancerFocusId,
    filteredExperts,
    currentUser,
    analyticsSource: 'directory',
    maxItems: 2,
    isDisabled: isSectionDisabled,
    isLoadingFilteredExperts,
    isFetchingFilteredExperts,
    isDraft,
  });

  const expertIds = useWatch({ control, name: 'expertIds' });

  const hasSelectedExperts = (expertIds?.length || 0) > 0;

  const isCheckboxDisabled =
    isSectionDisabled ||
    (!isLoadingFilteredExperts && !hasAdditionalExperts && !hasSelectedExperts);

  useEffect(() => {
    if (isSectionDisabled) {
      setIsExpertMultiSelectOpen(false);
    }
  }, [isSectionDisabled, setIsExpertMultiSelectOpen]);

  return (
    <VStack className="rounded-md bg-background-50 p-4">
      {/* Alternative without background: <VStack className="mt-4"> */}
      <Controller
        name="enableExpertSelection"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Checkbox
            value={value ? 'true' : 'false'}
            onChange={(checked) => {
              onChange(checked);

              handleSendToAdditionalChange(checked);
            }}
            isChecked={value}
            isDisabled={isCheckboxDisabled}
            aria-label="Send to additional experts (optional)">
            <CheckboxIndicator>
              <CheckboxIcon as={CheckIcon} />
            </CheckboxIndicator>
            <CheckboxLabel
              className={twJoin(
                'text-sm',
                isCheckboxDisabled ? 'text-typography-400' : 'text-typography-500'
              )}>
              Send to additional experts (optional)
            </CheckboxLabel>
          </Checkbox>
        )}
      />

      {!!cancerFocusId && !isLoadingFilteredExperts && !hasAdditionalExperts && (
        <VStack className="mt-3">
          <Text className="text-xs text-typography-500">
            We currently have one expert available in{' '}
            {cancerTypeLabel ? cancerTypeLabel : 'this cancer type'}. Know someone great we should
            consider?{' '}
            <LinkText
              onPress={supportPrompt.open}
              className="text-xs text-primary-500 underline hover:text-primary-600">
              Recommend an expert
            </LinkText>{' '}
            or email us at{' '}
            <LinkText
              onPress={() => void openEmailLink(GENERAL_EMAIL_LINK)}
              className="text-xs text-primary-500 underline hover:text-primary-600">
              {GENERAL_EMAIL_ADDRESS}
            </LinkText>{' '}
            — we'd love to hear from you!
          </Text>
        </VStack>
      )}

      {enableExpertSelection && (hasAdditionalExperts || hasSelectedExperts) && (
        <VStack className="mt-3">
          <Controller
            name="expertIds"
            control={control}
            render={({ field: { onChange, value }, fieldState, formState }) => {
              return (
                <FormControl isInvalid={!!errors.expertIds?.message} isDisabled={isSectionDisabled}>
                  <ExpertMultiSelect
                    value={value || []}
                    options={realExperts}
                    onChange={onChange}
                    placeholder="Select up to 2 additional experts"
                    maxItems={2}
                    isLoading={isLoadingFilteredExperts}
                    isDisabled={isSectionDisabled}
                    currentUser={currentUser}
                    onClose={handleExpertMultiSelectClose}
                    isOpen={isExpertMultiSelectOpen}
                    onSelectedItemPress={() => setIsExpertMultiSelectOpen(true)}
                  />

                  <FormControlError className="mt-2">
                    <FormControlErrorIcon />
                    <FormControlErrorText size="xs">
                      {errors.expertIds?.message}
                    </FormControlErrorText>
                  </FormControlError>
                </FormControl>
              );
            }}
          />
        </VStack>
      )}

      <SupportForm
        isOpen={supportPrompt.isFormOpen}
        onClose={supportPrompt.closeForm}
        initialValues={{
          name:
            currentUser?.firstName && currentUser?.lastName
              ? `${currentUser.firstName} ${currentUser.lastName}`
              : '',
          email: currentUser?.email || '',
          reason: 'Recommend an Expert',
          message: 'Hello Primum,\n\nI would like to recommend an expert.',
        }}
      />
    </VStack>
  );
}
